// Order API
import queryString from 'query-string'
import {
    RequestConfigFunction,
} from 'app/types/request.types'
import {
    CancellationInfo,
} from 'app/Apps/OrderManagement/Orders/orders.types'
import {
    OrderModificationType,
} from '../../enums/label.enums'

/**
 * enum belongs to this API
 */
export enum DailyOpsQueryNames {
    toBeConfirmed = 'toBeConfirmed',
    deliveryViaRoadInfo = 'deliveryViaRoadInfo',
    deliveryViaRoadAddPickup = 'deliveryViaRoadAddPickup',
    deliveryViaRoadConfirmation = 'deliveryViaRoadConfirmation',
    pickupViaRoadConfirmation = 'pickupViaRoadConfirmation',
    airTransportByCustomerInfo = 'airTransportByCustomerInfo',
    confirmationOfDropOffInfo = 'confirmationOfDropOffInfo',
    confirmationOfUnloadingInfo = 'confirmationOfUnloadingInfo',
    missingInformation = 'missingInformation',
    containerSelection = 'containerSelection',
    missingActualPickup = 'missingActualPickup',
    missingActualDelivery = 'missingActualDelivery',
    changePendingApproval = 'changePendingApproval'
}

type OrderRequests = {
    // Filter requests
    customerTransports: RequestConfigFunction<undefined>,
    internalTransports: RequestConfigFunction<undefined>,
    openBookingsFilterRequest: RequestConfigFunction<Record<string, any>>,
    missingBillingCompany: RequestConfigFunction<Record<string, any>>,
    missingBillingCompanyCount: RequestConfigFunction<{ fieldName: string }>,
    openBookingsCount: RequestConfigFunction<{ fieldName: string }>,
    customerTransportCount: RequestConfigFunction<{ fieldName: string }>,
    billingCompleted: RequestConfigFunction<Record<string, any>>,
    billingCompletedCount: RequestConfigFunction<{ fieldName: string }>,
    allCustomerTransport: RequestConfigFunction<Record<string, any>>,
    allCustomerTransportCount: RequestConfigFunction<{ fieldName: string }>,
    extraDaysCreated: RequestConfigFunction<Record<string, any>>,
    extraDaysCreatedCount: RequestConfigFunction<{ fieldName: string }>,
    cancelledBilling: RequestConfigFunction<Record<string, any>>,
    cancelledBillingCount: RequestConfigFunction<{ fieldName: string }>,
    requiresManualBillingReview: RequestConfigFunction<Record<string, any>>,
    requiresManualBillingReviewCount: RequestConfigFunction<{ fieldName: string }>,
    baseLeaseCreated: RequestConfigFunction<Record<string, any>>,
    baseLeaseCreatedCount: RequestConfigFunction<{ fieldName: string }>,
    extraDaysToBeCreated: RequestConfigFunction<Record<string, any>>,
    extraDaysToBeCreatedCount: RequestConfigFunction<{ fieldName: string }>,
    containerSelectionCustomerTransportsCount: RequestConfigFunction<{ fieldName: string }>,
    containerSelectionInternalTransportsCount: RequestConfigFunction<{ fieldName: string }>,
    previousOrders: RequestConfigFunction<{ orderNumber: string, params?: Record<string, any> }>,
    previousOrdersByLocation: RequestConfigFunction<{ locationId: number }>,
    previousOrdersByLocationCount: RequestConfigFunction<{ locationId: number, fieldName: string }>,
    dailyOps: RequestConfigFunction<{ queryName: DailyOpsQueryNames }>,
    dailyOpsCount: RequestConfigFunction<{ queryName: string, fieldName: string }>,
    allInternalTransport: RequestConfigFunction<Record<string, any>>,
    allInternalTransportCount: RequestConfigFunction<{ fieldName: string }>,

    // Order operations
    getOrder: RequestConfigFunction<{ orderNumber: string }>,
    getOrderById: RequestConfigFunction<{ id: string }>,
    getFullOrder: RequestConfigFunction<{ orderNumber: string }>,
    getBillingInformation: RequestConfigFunction<{ orderNumber: string }>,
    getModifications: RequestConfigFunction<{ orderNumber: string }>,
    getGenericData: RequestConfigFunction<{ orderNumber: string }>,
    updateGenericData: RequestConfigFunction<{ orderNumber: string }>,
    updateData: RequestConfigFunction<{
        orderNumber: string, data: Record<string, any>, modificationType?: OrderModificationType
    }>,
    updateLeaseStartTimestamp: RequestConfigFunction<{ orderNumber: string }>,
    updateOrderStep: RequestConfigFunction<{
        orderNumber: string,
        orderStepId: number,
        modificationType?: string,
        data: Record<string, any>
    }>,
    confirmStatus: RequestConfigFunction<{ orderNumber: string }>,
    confirmShipment: RequestConfigFunction<{ orderNumber: string }>,
    cancelOrder: RequestConfigFunction<{ orderNumber: string, data: CancellationInfo }>,
    postponeOrder: RequestConfigFunction<{ orderNumber: string, data: Record<string, any> }>,
    updateLane: RequestConfigFunction<{
        orderNumber: string,
        data: Record<string, any>,
        modificationType?: OrderModificationType
    }>,
    updateCollectionDropoffPoint:
        RequestConfigFunction<{ orderNumber: string, data?: Record<string, any> }>,
    sendToSkymind: RequestConfigFunction<{ orderNumber: string }>,
    feesResolution: RequestConfigFunction<{ orderNumber: string }>,
    truckingCapacityInbound: RequestConfigFunction<undefined>,
    truckingCapacityOutbound: RequestConfigFunction<undefined>,

    // Transport operations
    createCustomerTransport: RequestConfigFunction<{ data: Record<string, any> }>,
    createInternalTransport: RequestConfigFunction<{ data: Record<string, any> }>,

    // Positioning orders
    getMatchingPositioningOrders: RequestConfigFunction<{ orderNumber: string }>,
    getSharedPositioningOrders: RequestConfigFunction<{ orderNumber: string }>,
    createSharedPositioningOrders: RequestConfigFunction<{
        orderNumber: string, data?: Record<string, any>
    }>,

    // Postponement
    getPostponement: RequestConfigFunction<{ orderNumber: string }>,

    // Link
    getOrderLinkData: RequestConfigFunction<{ orderId: number }>,
}

const orderRequests: OrderRequests = {
    // Filter requests
    customerTransports: () => {
        return {
            url: 'order/filter-request/container-selection-customer-transports',
            method: 'POST', // Not used, ContainerSelection uses the URL only
        }
    },
    internalTransports: () => {
        return {
            url: 'order/filter-request/container-selection-internal-transports',
            method: 'POST', // Not used, ContainerSelection uses the URL only
        }
    },
    openBookingsFilterRequest: (params = {}) => {
        return {
            url: 'order/filter-request/open-bookings',
            method: 'POST',
            ...params,
        }
    },

    // Filter requests
    missingBillingCompany: (params = {}) => {
        return {
            url: 'order/filter-request/missing-billing-company',
            method: 'POST',
            ...params,
        }
    },
    missingBillingCompanyCount: ({
        fieldName,
    }) => {
        return {
            url: `order/filter-request/missing-billing-company/count/${fieldName}`,
            method: 'POST',
        }
    },
    openBookingsCount: ({
        fieldName,
    }) => {
        return {
            url: `order/filter-request/open-bookings/count/${fieldName}`,
            method: 'POST',
        }
    },
    customerTransportCount: ({
        fieldName,
    }) => {
        return {
            url: `order/filter-request/customer-transport/count/${fieldName}`,
            method: 'POST',
        }
    },
    billingCompleted: (params = {}) => {
        return {
            url: 'order/filter-request/billing-completed',
            method: 'POST',
            ...params,
        }
    },
    billingCompletedCount: ({
        fieldName,
    }) => {
        return {
            url: `order/filter-request/billing-completed/count/${fieldName}`,
            method: 'POST',
        }
    },
    allCustomerTransport: (params = {}) => {
        return {
            url: 'order/filter-request/all-customer-transport',
            method: 'POST',
            ...params,
        }
    },
    allCustomerTransportCount: ({
        fieldName,
    }) => {
        return {
            url: `order/filter-request/all-customer-transport/count/${fieldName}`,
            method: 'POST',
        }
    },
    extraDaysCreated: (params = {}) => {
        return {
            url: 'order/filter-request/extra-days-created',
            method: 'POST',
            ...params,
        }
    },
    extraDaysCreatedCount: ({
        fieldName,
    }) => {
        return {
            url: `order/filter-request/extra-days-created/count/${fieldName}`,
            method: 'POST',
        }
    },
    cancelledBilling: (params = {}) => {
        return {
            url: 'order/filter-request/cancelled-billing',
            method: 'POST',
            ...params,
        }
    },
    cancelledBillingCount: ({
        fieldName,
    }) => {
        return {
            url: `order/filter-request/cancelled-billing/count/${fieldName}`,
            method: 'POST',
        }
    },
    requiresManualBillingReview: (params = {}) => {
        return {
            url: 'order/filter-request/requires-manual-billing-review',
            method: 'POST',
            ...params,
        }
    },
    requiresManualBillingReviewCount: ({
        fieldName,
    }) => {
        return {
            url: `order/filter-request/requires-manual-billing-review/count/${fieldName}`,
            method: 'POST',
        }
    },
    baseLeaseCreated: (params = {}) => {
        return {
            url: 'order/filter-request/base-lease-created',
            method: 'POST',
            ...params,
        }
    },
    baseLeaseCreatedCount: ({
        fieldName,
    }) => {
        return {
            url: `order/filter-request/base-lease-created/count/${fieldName}`,
            method: 'POST',
        }
    },
    extraDaysToBeCreated: (params = {}) => {
        return {
            url: 'order/filter-request/extra-days-to-be-created',
            method: 'POST',
            ...params,
        }
    },
    extraDaysToBeCreatedCount: ({
        fieldName,
    }) => {
        return {
            url: `order/filter-request/extra-days-to-be-created/count/${fieldName}`,
            method: 'POST',
        }
    },
    containerSelectionCustomerTransportsCount: ({
        fieldName,
    }) => {
        return {
            url: `order/filter-request/container-selection-customer-transports/count/${fieldName}`,
            method: 'POST',
        }
    },
    containerSelectionInternalTransportsCount: ({
        fieldName,
    }) => {
        return {
            url: `order/filter-request/container-selection-internal-transports/count/${fieldName}`,
            method: 'POST',
        }
    },
    previousOrders: ({
        orderNumber,
        params = {},
    }) => {
        return {
            url: `order/${orderNumber}/previous-orders`,
            method: 'POST',
            ...params,
        }
    },
    previousOrdersByLocation: ({
        locationId,
    }) => {
        return {
            url: `order/filter-request/previous-orders/${locationId}`,
            method: 'POST',
        }
    },
    previousOrdersByLocationCount: ({
        locationId, fieldName,
    }) => {
        return {
            url: `order/filter-request/previous-orders/${locationId}/count/${fieldName}`,
            method: 'POST',
        }
    },
    dailyOps: ({
        queryName,
    }) => {
        return {
            url: `order/filter-request/daily-ops-${queryName}`,
            method: 'POST',
        }
    },
    dailyOpsCount: ({
        queryName, fieldName,
    }) => {
        return {
            url: `order/filter-request/daily-ops-${queryName}/count/${fieldName}`,
            method: 'POST',
        }
    },
    allInternalTransport: (params = {}) => {
        return {
            url: 'order/filter-request/all-internal-transport',
            method: 'POST',
            ...params,
        }
    },
    allInternalTransportCount: ({
        fieldName,
    }) => {
        return {
            url: `order/filter-request/all-internal-transport/count/${fieldName}`,
            method: 'POST',
        }
    },

    // Order operations
    getOrder: ({
        orderNumber,
    }) => {
        return {
            url: `order/${orderNumber}`,
            method: 'GET',
        }
    },
    getFullOrder: ({
        orderNumber,
    }) => {
        return {
            url: `order/${orderNumber}/full`,
            method: 'GET',
        }
    },
    getBillingInformation: ({
        orderNumber,
    }) => {
        return {
            url: `order/${orderNumber}/billing-information`,
            method: 'GET',
        }
    },
    getModifications: ({
        orderNumber,
    }) => {
        return {
            url: `order/${orderNumber}/modifications`,
            method: 'GET',
        }
    },
    getGenericData: ({
        orderNumber,
    }) => {
        return {
            url: `order/${orderNumber}/generic-data`,
            method: 'GET',
        }
    },
    updateGenericData: ({
        orderNumber,
    }) => {
        return {
            url: `order/${orderNumber}/generic-data`,
            method: 'PATCH',
        }
    },
    updateData: ({
        orderNumber,
        data,
        modificationType,
    }) => {
        return {
            url: queryString.stringifyUrl({
                url: `order/${orderNumber}/data`,
                query: modificationType ? {
                    modificationType,
                } : undefined,
            }),
            data,
            method: 'PATCH',
        }
    },
    updateLeaseStartTimestamp: ({
        orderNumber,
    }) => {
        return {
            url: `order/${orderNumber}/generic-data/leasestarttimestamp`,
            method: 'PATCH',
        }
    },
    updateOrderStep: ({
        orderNumber, orderStepId, modificationType, data,
    }) => {
        return {
            url: queryString.stringifyUrl({
                url: `order/${orderNumber}/order-step/${orderStepId}`,
                query: modificationType ? {
                    modificationType,
                } : undefined,
            }),
            data,
            method: 'PUT',
        }
    },
    confirmStatus: ({
        orderNumber,
    }) => {
        return {
            url: `order/${orderNumber}/status/confirmed`,
            method: 'PATCH',
        }
    },
    confirmShipment: ({
        orderNumber,
    }) => {
        return {
            url: `order/${orderNumber}/shipment-confirmation`,
            method: 'PATCH',
        }
    },
    cancelOrder: ({
        orderNumber,
        data,
    }) => {
        return {
            url: `order/${orderNumber}/cancellation`,
            method: 'PATCH',
            data,
        }
    },
    postponeOrder: ({
        orderNumber,
        data,
    }) => {
        return {
            url: `order/${orderNumber}/postponement`,
            method: 'POST',
            data,
        }
    },
    updateLane: ({
        orderNumber,
        modificationType,
        data = {},
    }) => {
        return {
            url: queryString.stringifyUrl({
                url: `order/${orderNumber}/lane-update`,
                query: modificationType ? {
                    modificationType,
                } : undefined,
            }),
            data,
            method: 'PATCH',
        }
    },
    updateCollectionDropoffPoint: ({
        orderNumber,
        data,
    }) => {
        return {
            url: `order/${orderNumber}/collectiondropoffpoint`,
            method: 'PATCH',
            data,
        }
    },
    sendToSkymind: ({
        orderNumber,
    }) => {
        return {
            url: `order/${orderNumber}/skymindsending`,
            method: 'POST',
        }
    },
    feesResolution: ({
        orderNumber,
    }) => {
        return {
            url: `order/${orderNumber}/feesresolution`,
            method: 'POST',
        }
    },
    truckingCapacityInbound: () => {
        return {
            url: 'order/filter-request/trucking-capacity-inbound',
            method: 'POST',
        }
    },
    truckingCapacityOutbound: () => {
        return {
            url: 'order/filter-request/trucking-capacity-outbound',
            method: 'POST',
        }
    },

    // Transport operations
    createCustomerTransport: ({
        data,
    }) => {
        return {
            url: 'order/customer-transport',
            method: 'POST',
            data,
        }
    },
    createInternalTransport: ({
        data,
    }) => {
        return {
            url: 'order/internal-transport',
            method: 'POST',
            data,
        }
    },

    // Positioning orders
    getMatchingPositioningOrders: ({
        orderNumber,
    }) => {
        return {
            url: `order/${orderNumber}/matching-positioning-orders`,
            method: 'GET',
        }
    },
    getSharedPositioningOrders: ({
        orderNumber,
    }) => {
        return {
            url: `order/${orderNumber}/shared-positioning-orders`,
            method: 'GET',
        }
    },
    createSharedPositioningOrders: ({
        orderNumber,
        data,
    }) => {
        return {
            url: `order/${orderNumber}/shared-positioning-orders`,
            method: 'POST',
            data,
        }
    },
    getPostponement: ({
        orderNumber,
    }) => {
        return {
            url: `order/${orderNumber}/postponement/all`,
            method: 'GET',
        }
    },
    getOrderById: ({
        id,
    }) => {
        return {
            url: `order/${id}`,
            method: 'GET',
        }
    },
    // Order link
    getOrderLinkData: ({
        orderId,
    }) => {
        return {
            url: `order/${orderId}/external-link`,
            method: 'GET',
        }
    },
}

export default orderRequests
